/* =========================================================
   Disassociated instructors.

   Instructors with no active site affiliation. From here the
   ETC Admin can assign one to any site in any organization
   (searchable org picker → site → primary role), quick-view
   their data without leaving the table, or remove them after
   an explicit confirmation.
   ========================================================= */
import React, { useReducer, useState, type ReactNode } from 'react';
import type { Instructor } from '@/types';
import { ic, sized } from '@/components/Icons';
import { BLUE_HEADER, DataTable, Modal, Pagination, Section, type Column } from '@/components/ui';
import { COUNTRIES, PRIMARY_ROLES, ds } from '@/data';
import { useI18n } from '@/i18n';
import { paginate, sortRows, useTableState, type SortMap } from '@/hooks/useTableState';
import { useGo } from '@/hooks/useRoute';
import { useAppStore } from '@/store/useAppStore';
import { instrInitials } from '@/screens/instructors/InstrAvatar';

type Row = Instructor & { _key: string };

const redAvatar = (size: number): React.CSSProperties => ({
  width: size, height: size, borderRadius: 99, background: 'var(--st-req-l)',
  color: 'var(--err)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontWeight: 700, fontSize: size * 0.37, flexShrink: 0,
});

export function Orphans() {
  const { lang, t } = useI18n();
  const go = useGo();
  const addToast = useAppStore((s) => s.addToast);
  const [, force] = useReducer((x: number) => x + 1, 0);

  const [confirm, setConfirm] = useState<Row | null>(null);
  const [quickView, setQuickView] = useState<Row | null>(null);
  const [assign, setAssign] = useState<Row | null>(null);
  const [aOrg, setAOrg] = useState('');
  const [aOrgQ, setAOrgQ] = useState('');
  const [aOrgOpen, setAOrgOpen] = useState(false);
  const [aSite, setASite] = useState('');
  const [aRole, setARole] = useState('Instructor');
  const [countryFilter, setCountryFilter] = useState('');
  const table = useTableState(10);

  const orphans = ds.listInstructors({ disassociated: true }).filter((i) => i.active !== false);

  const countryName = (code: string) => {
    const c = COUNTRIES.find((x) => x.code === code);
    return c ? (lang === 'es' ? c.name_es : c.name_en) : code || '';
  };

  function deactivate(id: string) {
    ds.deactivateInstructor(id);
    setConfirm(null);
    addToast({ kind: 'delete', title: lang === 'es' ? 'Instructor eliminado' : 'Instructor removed' });
    force();
  }

  function openAssign(r: Row) {
    setAssign(r);
    setAOrg('');
    setAOrgQ('');
    setAOrgOpen(false);
    setASite('');
    setARole('Instructor');
  }

  function doAssign() {
    if (!assign || !aSite) return;
    const asg = ds.addAssignment(assign.InstructorId, aSite, aRole);
    if (asg) {
      addToast({
        kind: 'save',
        title: lang === 'es' ? 'Instructor asignado' : 'Instructor assigned',
        body: `${assign.EnglishFirstName} ${assign.EnglishLastName} → ${asg.SiteName} · ${asg.OrgName}`,
      });
    }
    setAssign(null);
    force();
  }

  const countryOptions = Array.from(new Set(orphans.map((i) => i.Country).filter(Boolean)))
    .map((c) => ({ code: c, name: countryName(c) }))
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));

  const enriched: Row[] = orphans
    .filter((i) => !countryFilter || i.Country === countryFilter)
    .map((i) => ({ ...i, _key: i.InstructorId }));

  const sortValues: SortMap<Row> = {
    name: { type: 'text', get: (r) => `${r.EnglishLastName || ''} ${r.EnglishFirstName || ''}`.trim() },
    JobTitle: { type: 'text', get: (r) => r.JobTitle || '' },
    country: { type: 'text', get: (r) => countryName(r.Country) },
    cert: { type: 'text', get: (r) => r.Certification || '' },
    LastUpdated: { type: 'text', get: (r) => r.LastUpdated || '' },
  };

  const { rows, page, pageCount } = paginate(sortRows(enriched, table.sort, sortValues), table.page, table.per);

  const columns: Column<Row>[] = [
    {
      key: 'name',
      label: 'Instructor',
      sortable: true,
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={redAvatar(30)}>{instrInitials(r)}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{r.EnglishFirstName} {r.EnglishLastName}</div>
            <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{r.InstructorId}</div>
          </div>
        </div>
      ),
    },
    { key: 'JobTitle', label: lang === 'es' ? 'Cargo' : 'Title', muted: true, sortable: true },
    { key: 'country', label: lang === 'es' ? 'País' : 'Country', sortable: true, render: (r) => countryName(r.Country) },
    {
      key: 'cert',
      label: 'Cert.',
      sortable: true,
      render: (r) => (
        <span
          className="mono"
          style={{
            display: 'inline-block', padding: '3px 7px', borderRadius: 4,
            fontSize: 11, fontWeight: 800, letterSpacing: '.04em',
            color: 'var(--act-download-d)', background: 'rgba(46,126,238,.10)',
          }}
        >
          {r.Certification}
        </span>
      ),
    },
    { key: 'LastUpdated', label: lang === 'es' ? 'Última actualización' : 'Last updated', mono: true, sortable: true },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (r) => (
        <div style={{ display: 'inline-flex', gap: 6 }}>
          <button className="btn sm" data-tip={lang === 'es' ? 'Asignar a un site' : 'Assign to a site'} onClick={(e) => { e.stopPropagation(); openAssign(r); }}>
            {sized(ic.site, 13)} {lang === 'es' ? 'Asignar' : 'Assign'}
          </button>
          <button
            className="btn sm"
            data-tip={lang === 'es' ? 'Ver datos sin salir de la tabla' : 'View data without leaving the table'}
            onClick={(e) => { e.stopPropagation(); setQuickView(r); }}
          >
            {t('common.view')}
          </button>
          <button className="btn sm danger" onClick={(e) => { e.stopPropagation(); setConfirm(r); }}>{ic.trash}</button>
        </div>
      ),
    },
  ];

  const orgList = [...ds.listOrgs()].sort((a, b) => a.OrgName.localeCompare(b.OrgName, 'es'));
  const q = aOrgQ.trim().toLowerCase();
  const filteredOrgs = orgList.filter((o) => !q || (o.OrgName + ' ' + o.OrgId).toLowerCase().includes(q));

  return (
    <Section title={t('admin.orphans')} tone="var(--err)" right={<span className="chip err">{orphans.length}</span>}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 6, marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{t('admin.orphans_help')}</div>
        {orphans.length > 0 ? (
          <select
            value={countryFilter}
            onChange={(e) => { setCountryFilter(e.target.value); table.setPage(1); }}
            aria-label={lang === 'es' ? 'Filtrar por país' : 'Filter by country'}
            style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid var(--line-2)', fontSize: 12, background: 'var(--surface)', maxWidth: 180 }}
          >
            <option value="">{lang === 'es' ? 'Todos los países' : 'All countries'}</option>
            {countryOptions.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        ) : null}
      </div>

      {orphans.length === 0 ? (
        <div style={{ padding: 14, textAlign: 'center', color: 'var(--text-3)', fontSize: 13, lineHeight: 1.5 }}>
          {lang === 'es' ? 'Sin instructores desasociados.' : 'No disassociated instructors.'}
          <br />
          {/* Quien viene buscando a alguien concreto y no lo encuentra necesita
              saber qué contiene esta lista, no solo que está vacía (prueba 5.6). */}
          <span style={{ fontSize: 12 }}>
            {lang === 'es'
              ? 'Aquí solo entran los que se quedaron sin ninguna afiliación. A un instructor que aún pertenece a un site se llega desde Instructores.'
              : 'Only instructors left with no affiliation appear here. One who still belongs to a site is reached from Instructors.'}
          </span>
        </div>
      ) : (
        <>
          <DataTable
            empty={lang === 'es' ? 'Sin instructores desasociados para el país seleccionado.' : 'No disassociated instructors for the selected country.'}
            columns={columns}
            rows={rows}
            sort={table.sort}
            onSort={table.onSort}
            stickyHeader
            onRowClick={(r) => setQuickView(r)}
          />
          <Pagination page={page} pageCount={pageCount} onPage={table.setPage} total={enriched.length} per={table.per} onPer={table.onPer} />
        </>
      )}

      {/* delete confirmation */}
      <Modal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={lang === 'es' ? 'Eliminar instructor desasociado' : 'Remove disassociated instructor'}
        footer={
          <>
            <button className="btn" onClick={() => setConfirm(null)}>{t('common.cancel')}</button>
            <button className="btn danger" onClick={() => confirm && deactivate(confirm.InstructorId)}>
              {ic.trash} {lang === 'es' ? 'Sí, eliminar' : 'Yes, remove'}
            </button>
          </>
        }
      >
        {confirm ? (
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ color: 'var(--err)', flexShrink: 0, marginTop: 2 }}>{sized(ic.warn, 22)}</span>
            <div style={{ fontSize: 13.5, color: 'var(--text)', lineHeight: 1.5 }}>
              {lang === 'es' ? (
                <>
                  ¿Seguro que deseas eliminar a <strong>{confirm.EnglishFirstName} {confirm.EnglishLastName}</strong>{' '}
                  (<span className="mono">{confirm.InstructorId}</span>)? Esta acción lo desactiva del sistema y no se puede deshacer.
                </>
              ) : (
                <>
                  Are you sure you want to remove <strong>{confirm.EnglishFirstName} {confirm.EnglishLastName}</strong>{' '}
                  (<span className="mono">{confirm.InstructorId}</span>)? This deactivates them from the system and cannot be undone.
                </>
              )}
            </div>
          </div>
        ) : null}
      </Modal>

      {/* floating quick-view: compact instructor data, no navigation */}
      <Modal
        open={!!quickView}
        onClose={() => setQuickView(null)}
        width={480}
        headerStyle={BLUE_HEADER}
        title={lang === 'es' ? 'Datos del instructor' : 'Instructor data'}
        footer={
          <>
            <button
              className="btn"
              onClick={() => {
                const r = quickView;
                setQuickView(null);
                if (r) go('instructorDetail', { instructorId: r.InstructorId });
              }}
            >
              {sized(ic.arrowR, 13)} {lang === 'es' ? 'Abrir ficha completa' : 'Open full record'}
            </button>
            <button className="btn primary" onClick={() => setQuickView(null)}>{lang === 'es' ? 'Cerrar' : 'Close'}</button>
          </>
        }
      >
        {quickView ? <QuickView r={quickView} countryName={countryName} /> : null}
      </Modal>

      {/* assign to a site */}
      <Modal
        open={!!assign}
        onClose={() => setAssign(null)}
        headerStyle={BLUE_HEADER}
        title={lang === 'es' ? 'Asignar instructor a un site' : 'Assign instructor to a site'}
        footer={
          <>
            <button className="btn" onClick={() => setAssign(null)}>{t('common.cancel')}</button>
            <button className="btn primary" disabled={!aSite} onClick={doAssign}>
              {ic.check} {lang === 'es' ? 'Asignar al site' : 'Assign to site'}
            </button>
          </>
        }
      >
        {assign ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--line)' }}>
              <span style={redAvatar(32)}>{instrInitials(assign)}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5 }}>{assign.EnglishFirstName} {assign.EnglishLastName}</div>
                <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{assign.InstructorId}</div>
              </div>
              <span className="chip err" style={{ marginLeft: 'auto', flexShrink: 0 }}>{t('common.orphan')}</span>
            </div>

            {/* searchable organization picker */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>{lang === 'es' ? 'Organización' : 'Organization'}</span>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: aOrg ? 'var(--ok)' : 'var(--text-3)', display: 'inline-flex', pointerEvents: 'none' }}>
                  {sized(aOrg ? ic.check : ic.search, 14)}
                </span>
                <input
                  value={aOrgQ}
                  placeholder={lang === 'es' ? 'Buscar organización…' : 'Search organization…'}
                  onChange={(e) => {
                    setAOrgQ(e.target.value);
                    setAOrgOpen(true);
                    if (aOrg) { setAOrg(''); setASite(''); }
                  }}
                  onFocus={() => setAOrgOpen(true)}
                  onBlur={() => window.setTimeout(() => setAOrgOpen(false), 120)}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '7px 28px 7px 29px', borderRadius: 6, border: '1px solid var(--line-2)', fontSize: 13, background: 'var(--surface)', fontFamily: 'inherit', color: 'var(--text)' }}
                />
                {aOrg || aOrgQ ? (
                  <button
                    type="button"
                    aria-label="clear"
                    onMouseDown={(e) => { e.preventDefault(); setAOrg(''); setASite(''); setAOrgQ(''); setAOrgOpen(true); }}
                    style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-3)', display: 'inline-flex', padding: 2 }}
                  >
                    {sized(ic.close, 13)}
                  </button>
                ) : null}
                {aOrgOpen ? (
                  <div className="card" style={{ position: 'absolute', zIndex: 30, top: 'calc(100% + 4px)', left: 0, right: 0, maxHeight: 192, overflowY: 'auto', boxShadow: 'var(--shadow-3)', padding: 4 }}>
                    {filteredOrgs.length === 0 ? (
                      <div style={{ padding: 10, fontSize: 12.5, color: 'var(--text-3)' }}>
                        {lang === 'es' ? 'Sin resultados para' : 'No results for'} “{aOrgQ}”
                      </div>
                    ) : (
                      filteredOrgs.map((o) => (
                        <button
                          key={o.OrgId}
                          type="button"
                          className="btn ghost"
                          onMouseDown={(e) => { e.preventDefault(); setAOrg(o.OrgId); setAOrgQ(o.OrgName); setASite(''); setAOrgOpen(false); }}
                          style={{ width: '100%', justifyContent: 'space-between', padding: '7px 8px', fontWeight: aOrg === o.OrgId ? 700 : 500, borderRadius: 6, fontSize: 12.5 }}
                        >
                          <span className="truncate" style={{ textAlign: 'left' }}>{o.OrgName}</span>
                          <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-3)', flexShrink: 0, marginLeft: 8 }}>
                            {ds.listSites({ orgId: o.OrgId }).length} sites
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                ) : null}
              </div>
            </div>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>Site</span>
              <select
                value={aSite}
                onChange={(e) => setASite(e.target.value)}
                disabled={!aOrg}
                style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--line-2)', fontSize: 13, background: aOrg ? 'var(--surface)' : 'var(--bg-2)', cursor: aOrg ? 'pointer' : 'not-allowed' }}
              >
                <option value="">
                  {aOrg
                    ? lang === 'es' ? '— Seleccionar site —' : '— Select site —'
                    : lang === 'es' ? 'Primero selecciona una organización' : 'Select an organization first'}
                </option>
                {aOrg
                  ? [...ds.listSites({ orgId: aOrg })].sort((a, b) => a.SiteName.localeCompare(b.SiteName, 'es')).map((s) => (
                      <option key={s.SiteId} value={s.SiteId}>
                        {s.SiteName}{s.SiteCountry ? ` (${s.SiteCountry})` : ''}
                      </option>
                    ))
                  : null}
              </select>
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>{lang === 'es' ? 'Rol primario' : 'Primary role'}</span>
              <select
                value={aRole}
                onChange={(e) => setARole(e.target.value)}
                style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--line-2)', fontSize: 13, background: 'var(--surface)' }}
              >
                {['Instructor', 'Lead Instructor', ...PRIMARY_ROLES.filter((r) => r !== 'Instructor' && r !== 'Lead Instructor')].map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </label>

            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: 'var(--text-2)', lineHeight: 1.45 }}>
              <span style={{ color: 'var(--info)', flexShrink: 0, marginTop: 1 }}>{sized(ic.info, 15)}</span>
              <span>
                {lang === 'es'
                  ? 'Al asignarlo, el instructor dejará de estar desasociado y aparecerá como activo en el site seleccionado.'
                  : 'Once assigned, the instructor will no longer be disassociated and will appear as active in the selected site.'}
              </span>
            </div>
          </div>
        ) : null}
      </Modal>
    </Section>
  );
}

/* Fuera del componente a propósito: definida dentro, cada render creaba
   un tipo nuevo y React remontaba el subárbol. Aquí solo hay texto y no
   se notaba, pero es el mismo defecto que hacía imposible teclear en el
   formulario de usuarios. */
function Item({ label, value, mono }: { label: ReactNode; value?: string; mono?: boolean }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 3 }}>{label}</div>
      <div className={(mono ? 'mono ' : '') + 'truncate'} style={{ fontSize: 13, color: value ? 'var(--text)' : 'var(--text-3)' }}>{value || '—'}</div>
    </div>
  );
}

function QuickView({ r, countryName }: { r: Row; countryName: (c: string) => string }) {
  const { lang, t } = useI18n();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={redAvatar(44)}>{instrInitials(r)}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: '-0.01em' }}>{r.EnglishFirstName} {r.EnglishLastName}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, flexWrap: 'wrap' }}>
            <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>{r.InstructorId}</span>
            <span className="chip err" style={{ fontSize: 9, background: 'transparent' }}>{t('common.orphan')}</span>
          </div>
        </div>
      </div>
      <div
        style={{
          display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px 16px',
          padding: '14px 16px', background: 'var(--surface-2)', borderRadius: 10, border: '1px solid var(--line)',
        }}
      >
        <Item label={lang === 'es' ? 'Cargo' : 'Job title'} value={r.JobTitle} />
        <Item label={lang === 'es' ? 'País' : 'Country'} value={countryName(r.Country)} />
        <Item label={lang === 'es' ? 'Certificación' : 'Certification'} value={r.Certification} />
        <Item label={lang === 'es' ? 'Roles de acceso' : 'Access roles'} value={r.AccessRoles} />
        <Item label="Email" value={r.EmailAddress} mono />
        <Item label={lang === 'es' ? 'Móvil' : 'Mobile'} value={r.Mobile1} mono />
        <Item label={lang === 'es' ? 'Teléfono' : 'Telephone'} value={r.Telephone1} mono />
        <Item label={lang === 'es' ? 'Año de ingreso' : 'Year joined'} value={r.YearJoined} mono />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-3)' }}>
        {sized(ic.info, 12)}
        <span>
          {lang === 'es' ? 'Última actualización' : 'Last updated'}: <span className="mono">{r.LastUpdated || '—'}</span>
        </span>
      </div>
    </div>
  );
}
